import React from 'react';
import { ActivityIcon, BellIcon, FolderIcon, HeartPulseIcon } from 'lucide-react';
import { MaternityFile } from '../../types';
import { countMaternityWardStages, MaternityWardFilter, maternityWardFilterLabels } from './maternityWard';
type MaternityWardStatsProps = {
  files: MaternityFile[];
  activeFilter: MaternityWardFilter;
  onSelect: (filter: MaternityWardFilter) => void;
};
export function MaternityWardStats({
  files,
  activeFilter,
  onSelect
}: MaternityWardStatsProps) {
  const counts = countMaternityWardStages(files);
  const cards: Array<{
    id: MaternityWardFilter;
    label: string;
    icon: typeof FolderIcon;
    accent: string;
    hint: string;
  }> = [{
    id: 'all',
    label: maternityWardFilterLabels.all,
    icon: FolderIcon,
    accent: 'text-gray-900',
    hint: 'On the ward now'
  }, {
    id: 'antenatal',
    label: maternityWardFilterLabels.antenatal,
    icon: HeartPulseIcon,
    accent: 'text-blue-600',
    hint: 'Awaiting labour'
  }, {
    id: 'labour',
    label: maternityWardFilterLabels.labour,
    icon: ActivityIcon,
    accent: 'text-pink-600',
    hint: 'Active labour'
  }];
  const postnatalSelected = activeFilter === 'postnatal';
  const hasDue = counts.postnatal > 0;
  return <section className="grid grid-cols-2 gap-4 lg:grid-cols-4" aria-label="Maternity ward summary" data-id="element-4127" data-mp="3qpt8bmaslfs4">
      {cards.map(({
      id,
      label,
      icon: Icon,
      accent,
      hint
    }) => {
      const selected = activeFilter === id;
      return <button key={id} type="button" aria-pressed={selected} onClick={() => onSelect(id)} className={`rounded-xl border bg-white p-5 text-left shadow-sm transition-colors hover:border-pink-300 focus:outline-none focus:ring-2 focus:ring-pink-500 ${selected ? 'border-pink-500 ring-1 ring-pink-500' : 'border-gray-200'}`} data-id="element-4128" data-mp="2xyt2r6wtc3yc">
            <div className="flex items-start justify-between gap-3" data-id="element-4129" data-mp="2f8bbz7vjjifm">
              <p className="text-sm font-medium text-gray-600" data-id="element-4130" data-mp="3ik0j8iyv5k26">{label}</p>
              <Icon className={`h-4 w-4 ${accent}`} data-id="element-4131" data-mp="3dmqk2blo9u15" />
            </div>
            <p className={`mt-2 text-3xl font-bold ${accent}`} data-id="element-4132" data-mp="3rdlz65mrl7d6">{counts[id]}</p>
            <p className="mt-1 text-xs text-gray-500" data-id="element-4133" data-mp="3rfq0qgx6xuaa">
              {selected ? 'Showing these files' : hint}
            </p>
          </button>;
    })}

      <button type="button" aria-pressed={postnatalSelected} onClick={() => onSelect('postnatal')} className={`rounded-xl border p-5 text-left shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500 ${postnatalSelected ? 'border-amber-500 bg-amber-50 ring-1 ring-amber-500' : hasDue ? 'border-amber-300 bg-amber-50 hover:border-amber-400' : 'border-gray-200 bg-white hover:border-amber-300'}`} data-id="element-4134" data-mp="2znh51f97d5yr">
        <div className="flex items-start justify-between gap-3" data-id="element-4135" data-mp="ixk6y281qwmi">
          <p className={`text-sm font-medium ${hasDue ? 'text-amber-900' : 'text-gray-600'}`} data-id="element-4136" data-mp="rdrg1jwq2gh2">
            Postnatal
          </p>
          <BellIcon className={`h-4 w-4 ${hasDue ? 'text-amber-700' : 'text-gray-400'}`} data-id="element-4137" data-mp="ve33t5kdswwr" />
        </div>
        <p className={`mt-2 text-3xl font-bold ${hasDue ? 'text-amber-800' : 'text-gray-900'}`} data-id="element-4138" data-mp="mf7uvlpyd8pr">
          {counts.postnatal}
        </p>
        <p className={`mt-1 text-xs ${hasDue ? 'text-amber-800' : 'text-gray-500'}`} data-id="element-4139" data-mp="1a7evwy6mi3vi">
          {postnatalSelected ? 'Showing files due for review' : hasDue ? 'Due for review — click to open' : 'No reviews due'}
        </p>
      </button>
    </section>;
}